import {shallowRef, ref, computed, watch, watchEffect} from 'vue'
import {defineStore} from 'pinia'
import {useRoute} from 'vue-router'

import sessionDataLoader from '@/utils/sessionDataLoader'
import {useDomTrimmingStore} from './domTrimming'
import {useSessionEventsStore} from './sessionEvents'
import {useStore} from './store'

export const useSessionStore = defineStore('session', () => {
	const route = useRoute()
	const session = ref()
	const sessionStartDate = computed(() => session.value && new Date(session.value.startedAt))
	const sessionUploadMethod = computed(() => session.value && session.value.uploadMethod)
	const dataLoader = shallowRef(sessionDataLoader)
	const sessionEventsStore = useSessionEventsStore()
	const store = useStore()

	// keep the session reference in the store in sync
	watchEffect(() => (store.sessionRef = session.value))

	/**********************
	 * VIDEO SESSIONS
	 */
	const videoStartDate = computed(() => {
		const videoStartEvent = sessionEventsStore.processedEvents.find(({type}) => type === 'videoStart')
		return videoStartEvent?.createdAt && new Date(videoStartEvent.createdAt)
	})
	const videoOffsetInMilliseconds = computed(() => {
		// Video recording can start with a delay compared to `session.startedAt`.
		// Therefore seek times need to be offset to the session time.
		if (videoStartDate.value) {
			return videoStartDate.value - sessionStartDate.value
		}
		// For DOM recordings, `session.startedAt` is synced with the DOM recording.
		// For all other sessions (screenshot, no media), there's no time offset / it's irrelevant.
		return 0
	})
	const videoDuration = ref()
	const videoDurationSeconds = ref()
	const videoDurationMilliseconds = ref()
	// video duration from the extension is stored in the session as milliseconds
	// but until PR #3676 the video duration from the SDK was stored in seconds
	watch(videoDuration, duration => {
		if (!duration) return
		if (duration < 1000) {
			videoDurationSeconds.value = duration
			videoDurationMilliseconds.value = duration * 1000
			return
		}
		videoDurationSeconds.value = Math.floor(duration / 1000)
		videoDurationMilliseconds.value = duration
	})
	function storePlayerTimeInSessionStorage(time, sessionId) {
		if (!sessionStorage) return
		sessionStorage.setItem(`video-player-time-${sessionId}`, time)
	}
	function retrievePlayerTimeFromSessionStorage(sessionId) {
		if (!sessionStorage) return
		const time = parseFloat(sessionStorage.getItem(`video-player-time-${sessionId}`))
		if (!isNaN(time) && time > 0) {
			return time
		}
	}
	function deletePlayerTimeFromSessionStorage(sessionId) {
		if (!sessionStorage) return
		sessionStorage.removeItem(`video-player-time-${sessionId}`)
	}
	const markers = ref({})

	/**********************
	 * INSTANT REPLAY SESSIONS
	 */
	const domTrimming = useDomTrimmingStore()

	/**********************
	 * SEEKING
	 */
	const canSeek = computed(() => {
		if (!session.value) return false
		return Boolean(session.value.video || session.value.domEvents)
	})

	const seekSeconds = ref()
	// Start the video at the time specified in `?seekSeconds=3.0` query parameter.
	watch(
		() => route.query.seekSeconds,
		seekSecondsRouteParam => {
			const seekSecondsNumber = Number(seekSecondsRouteParam)
			if (isNaN(seekSecondsNumber)) return
			seekSeconds.value = seekSecondsNumber
		}
	)

	function seek(eventOrNumber) {
		let seconds = eventOrNumber?.detail || eventOrNumber
		if (!canSeek.value) return
		if (seconds === null || seconds === undefined) return
		seconds = Number(seconds)
		if (isNaN(seconds)) return
		seekSeconds.value = seconds
		currentTimeSeconds.value = seconds
	}

	/**********************
	 * PLAYING
	 */
	const currentTimeSeconds = ref(0)

	/**********************
	 * MISC
	 */
	const canUserEditSession = computed(() => {
		if (!session.value) return false
		if (store.isExtension) return true
		// user must be member of workspace to be allowed to edit sessions
		if (!store.isUserWorkspaceMember) return false
		// other users cannot edit sessions in private collections
		if (!store.workspaceCollections.find(collection => collection.id === session.value.collectionId)) {
			return false
		}
		// user has product viewer role therefore cannot edit sessions
		if (store.hasUserProductViewerRole) return false
		// user reported the session and can therefore edit it independent of workspace role
		if (session.value.ownerId === store.user?.id) return true
		// bug reporters can only their own sessions
		if (store.hasUserBugReporterRole) return false
		return true
	})

	const workspaceUsersByIdAndEmail = computed(() => {
		return store.users.reduce((dictionary, user) => {
			dictionary.set(user.id, user)
			dictionary.set(user.email, user)
			return dictionary
		}, new Map())
	})

	function getReporter(session = session.value) {
		if (session.uploadMethod === 'extension') {
			return workspaceUsersByIdAndEmail.value.get(session.ownerId)
		}

		if (session.uploaderEmail) {
			const {uploaderEmail} = session
			const user = workspaceUsersByIdAndEmail.value.get(uploaderEmail)
			return user || {id: uploaderEmail, email: uploaderEmail}
		}

		return {id: 'Anonymous (via SDK)', name: 'Anonymous (via SDK)'}
	}

	function calculateMillisecondsSinceSessionStart(timeString) {
		let millisecondsSinceSessionStart = new Date(timeString) - sessionStartDate.value

		// Subtract recording pauses which ocurred before the timeString
		for (const timestamp in sessionEventsStore.durationsOfPausesByTimestamps) {
			if (timeString > timestamp) {
				millisecondsSinceSessionStart -= sessionEventsStore.durationsOfPausesByTimestamps[timestamp]
			}
		}
		return millisecondsSinceSessionStart
	}

	function $reset() {
		if (!session.value) return
		deletePlayerTimeFromSessionStorage(session.value.id)
		videoDuration.value = undefined
		videoDurationSeconds.value = undefined
		videoDurationMilliseconds.value = undefined
		markers.value = {}
		seekSeconds.value = undefined
		currentTimeSeconds.value = 0
		let resetSessionInterval = setInterval(() => {
			// Only unset session value in store once the route no longer references the store's session.
			// This is to avoid NPEs in components rendered on session views referencing session object properties like `session.public`.
			if (route?.params.sessionId === session.value?.id) return

			session.value = undefined
			clearInterval(resetSessionInterval)
		}, 50)
	}

	return {
		session,
		sessionStartDate,
		sessionUploadMethod,
		dataLoader,
		// video session
		videoOffsetInMilliseconds,
		markers,
		videoDuration,
		videoDurationSeconds,
		videoDurationMilliseconds,
		storePlayerTimeInSessionStorage,
		retrievePlayerTimeFromSessionStorage,
		// instant replay session
		domTrimming,
		// seeking
		canSeek,
		seekSeconds,
		seek,
		// playing
		currentTimeSeconds,
		// misc
		canUserEditSession,
		getReporter,
		calculateMillisecondsSinceSessionStart,
		$reset,
	}
})
